/**
 * Typed errors thrown by the API client. Callers can pattern-match on
 * the instance type or the `kind` discriminator to render the right UI.
 *
 * All extend `ApiError` so generic `catch (err) { if (err instanceof ApiError) … }`
 * is the universal handler.
 */
export class ApiError extends Error {
    kind = 'http';
    status;
    body;
    constructor(message, status, body) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
    }
}
/**
 * Best human-readable message for an error (audit medium).
 *
 * `unwrap()` synthesizes `ApiError.message` as a generic `API {status}
 * {statusText}` (e.g. "API 409 Conflict"), so reading `error.message` directly
 * hides the backend's real Convention-03 message — which lives in `error.body`.
 * Prefer `body.message` when present, then the top-level message, then a
 * caller-supplied fallback. Single source of truth for both portal + admin.
 */
export function messageOf(error, fallback = 'Something went wrong. Please try again.') {
    if (error instanceof ApiError) {
        const body = error.body && typeof error.body === 'object'
            ? error.body
            : null;
        if (typeof body?.message === 'string' && body.message.trim()) {
            return body.message.trim();
        }
        if (error.message)
            return error.message;
    }
    if (error instanceof Error && error.message)
        return error.message;
    return fallback;
}
/** 401 from the server. The client invokes the configured on401 handler
 *  BEFORE this throws — the throw is for code that wants to gate further
 *  rendering on the auth state. */
export class AuthRequiredError extends ApiError {
    kind = 'auth_required';
    constructor(body) {
        super('Session missing or expired', 401, body);
        this.name = 'AuthRequiredError';
    }
}
/**
 * Phase 14b-1-4 — typed error for the destructive-action TOTP step-up
 * flow. The backend's `TotpFreshGuard` (Phase 14a-5) returns
 * `401 { reason: 'totp_step_up_required' }` when a destructive admin
 * endpoint is hit without a fresh-enough TOTP. The api-client's `unwrap`
 * detects this discriminator and throws this subclass instead of the
 * generic `AuthRequiredError` so the admin app's MutationCache can
 * pattern-match `instanceof TotpStepUpRequiredError` to open the
 * step-up modal + enqueue the failing mutation for retry.
 *
 * The `reason` literal field is asserted in `errors.spec.ts` so a
 * refactor that drops it shows up as a type-level break, not a
 * runtime-only one.
 */
export class TotpStepUpRequiredError extends ApiError {
    kind = 'auth_required';
    reason = 'totp_step_up_required';
    constructor(body) {
        super('TOTP step-up required to perform this action', 401, body);
        this.name = 'TotpStepUpRequiredError';
        // Subclass prototype fix-up — without this, `instanceof` against
        // TotpStepUpRequiredError can fail under some downlevel TS targets.
        Object.setPrototypeOf(this, TotpStepUpRequiredError.prototype);
    }
}
/**
 * Discriminate a step-up-required error WITHOUT relying solely on
 * `instanceof TotpStepUpRequiredError`.
 *
 * Why a structural fallback exists: under Vite HMR (and certain bundler
 * code-splitting scenarios) the `TotpStepUpRequiredError` constructor
 * can be re-evaluated, producing two distinct constructor references.
 * The error thrown by api-client may be an instance of one; the
 * `instanceof` check in admin app code may be against the other. The
 * fallback shape check (`.status === 401 && .reason ===
 * 'totp_step_up_required'`) survives constructor-identity drift.
 *
 * **Single source of truth lives here in api-client** — admin/portal
 * consumers import this function instead of writing their own
 * structural checks. Future api-client refactors that rename `.status`
 * or `.reason` MUST update this helper too; the
 * `@truecare/api-client` errors spec asserts the field names so a
 * silent rename can't happen.
 */
export function isStepUpError(error) {
    if (error instanceof TotpStepUpRequiredError)
        return true;
    if (typeof error !== 'object' || error === null)
        return false;
    const maybe = error;
    return maybe.status === 401 && maybe.reason === 'totp_step_up_required';
}
/** Network-level failure (DNS, connection refused, CORS). status is 0. */
export class NetworkError extends ApiError {
    kind = 'network';
    constructor(message, cause) {
        super(message, 0, undefined);
        this.name = 'NetworkError';
        if (cause instanceof Error)
            this.cause = cause;
    }
}
/** Server returned a 2xx but the body wasn't parseable JSON, or didn't
 *  match the expected shape. */
export class ParseError extends ApiError {
    kind = 'parse';
    constructor(message, rawBody) {
        super(message, 0, rawBody);
        this.name = 'ParseError';
    }
}
//# sourceMappingURL=errors.js.map